import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import { type TransportConnection, type TransportOption, isFlightCardSavedItemData } from 'tripPlanner/api/types'
import { useSwitchDefaultTransportMutation } from 'tripPlanner/hooks/api/chat'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { buildDeleteItemMessage, buildFlightName } from '../utils/deleteTripMessages'
import TransportOptionItem, { getTransportIcon } from './TransportOptionItem'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import FlightSearchDrawer from './FlightSearchDrawer/FlightSearchDrawer'
import ShortlistedFlightsSection from './ShortlistedFlightsSection/ShortlistedFlightsSection'
import { CANVAS_CONTENT_WIDTH } from './constants'

const TransportContainer = styled.div`
  max-width: var(--itinerary-canvas-width, ${rem(CANVAS_CONTENT_WIDTH)});
  width: 100%;
`

const TransportContent = styled(Group)`
  min-width: 0;
`

const IconCircle = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
  font-size: ${rem(20)};
  position: relative;
  z-index: 2;
`

function formatDuration(option: TransportOption): string {
  return option.duration ? ` ~${option.duration}` : ''
}

interface Props {
  transport: TransportConnection
  onRefetchItinerary?: () => void
  onSendMessage?: (message: string) => void
}

function TripPlanTransportLine({ transport, onRefetchItinerary = noop, onSendMessage }: Props) {
  const intl = useIntl()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isFlightDrawerOpen, setIsFlightDrawerOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { mutate: switchDefault, isPending: isSwitching } = useSwitchDefaultTransportMutation()

  const defaultOption = useMemo((): TransportOption => {
    return transport.options.find((o) => o.savedItemData?.isDefaultTransport === true) ?? transport.options[0]
  }, [transport.options])

  const toggleDropdown = useCallback(() => {
    if (isSwitching) return
    setIsDropdownOpen(prev => !prev)
  }, [isSwitching])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleOptionClick = useCallback((itemId: number) => {
    closeDropdown()
    if (itemId === defaultOption.itemId) return

    switchDefault(
      { itemId },
      {
        onSuccess: onRefetchItinerary,
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: '03GxTY2YYsWsZc8Yxf64',
            defaultMessage: 'Failed to switch transport. Please try again.',
            description: 'Error snackbar message when switching the default transport option fails',
          }), 'critical')
        },
      },
    )
  }, [defaultOption.itemId, closeDropdown, switchDefault, onRefetchItinerary, intl])

  const handleDeleteItem = useCallback((itemId: number) => {
    if (!onSendMessage) return
    const option = transport.options.find((o) => o.itemId === itemId)
    if (!option) return
    onSendMessage(buildDeleteItemMessage({
      intl,
      item: { id: itemId, name: buildFlightName(option, transport.name) },
    }))
  }, [intl, onSendMessage, transport.name, transport.options])

  const hasShortlistedFlights = useMemo(
    () => transport.options.some(o => isFlightCardSavedItemData(o.savedItemData)),
    [transport.options],
  )

  const dropdownOptions = useMemo(
    () => transport.options.filter(o => !isFlightCardSavedItemData(o.savedItemData)),
    [transport.options],
  )
  const hasMultipleOptions = dropdownOptions.length > 1
  const typeLabel = defaultOption.label ?? defaultOption.type
  const DropdownIcon = isDropdownOpen ? LineAngleUpIcon : LineAngleDownIcon
  const normalizedType = defaultOption.type.toLowerCase().replace(/[_\s]/g, '')
  const isFlightTransport = normalizedType === 'flight' || normalizedType === 'leflight' || normalizedType === 'chatflight'

  const openFlightDrawer = useCallback(() => {
    setIsFlightDrawerOpen(true)
  }, [])

  const closeFlightDrawer = useCallback(() => {
    setIsFlightDrawerOpen(false)
  }, [])

  const searchFlightsLabel = intl.formatMessage({
    id: 'XJvcKtTTFLXU32DsNja_',
    defaultMessage: 'Search flights',
    description: 'Button label to open flight search drawer from the transport line in trip planner',
  })

  const label = (
    <BodyText variant="small">
      <BodyText variant="small" weight="bold" as="span">{typeLabel}</BodyText>
      {formatDuration(defaultOption)}
    </BodyText>
  )

  return (
    <TransportContainer>
      <Group direction="horizontal" gap={20} verticalAlign="center">
        <IconCircle>
          {defaultOption.emoji ?? getTransportIcon(defaultOption.type, 'S')}
        </IconCircle>
        <TransportContent direction="vertical" gap={8} fullWidth>
          <Group direction="horizontal" gap={12} verticalAlign="center" horizontalAlign="space-between">
            <Heading variant="heading4" as="span">{transport.name}</Heading>
            {isFlightTransport && (
              <TextButton
                kind="tertiary"
                size="small"
                startIcon={<LineSearchIcon />}
                onClick={openFlightDrawer}
              >
                {searchFlightsLabel}
              </TextButton>
            )}
          </Group>
          {!hasMultipleOptions && label}
          {hasMultipleOptions && <>
            <Clickable ref={triggerRef} onClick={toggleDropdown} disabled={isSwitching}>
              <Group direction="horizontal" gap={2} verticalAlign="center">
                {label}
                <DropdownIcon size="S" />
              </Group>
            </Clickable>
            <DropdownList
              size="S"
              open={isDropdownOpen}
              onClose={closeDropdown}
              triggerRef={triggerRef}
              anchorRef={triggerRef}
              portaled
              placement="bottom-start"
            >
              {dropdownOptions.map((option) => (
                <TransportOptionItem
                  key={option.itemId}
                  option={option}
                  isSelected={option.itemId === defaultOption.itemId}
                  onOptionClick={handleOptionClick}
                />
              ))}
            </DropdownList>
          </>}
        </TransportContent>
      </Group>
      {isFlightTransport && (
        <FlightSearchDrawer
          isOpen={isFlightDrawerOpen}
          onClose={closeFlightDrawer}
          originAirportCode={defaultOption.savedItemData?.departureAirport}
          destinationAirportCode={defaultOption.savedItemData?.arrivalAirport}
          onSendMessage={onSendMessage}
          existingFlights={transport.options}
        />
      )}
      {hasShortlistedFlights && (
        <ShortlistedFlightsSection
          transport={transport}
          onDeleteItem={handleDeleteItem}
        />
      )}
    </TransportContainer>
  )
}

export default TripPlanTransportLine
